import { gql } from "@apollo/client"
import { useCurrencySymbol } from "hooks"
import type { SymbolType } from "lib/apollo/types"
import { useEffect, useState } from "react"
import type { CampaignThresholdType } from "types"
import { getClient } from "lib/apolloClient"
import { withGlobalSpinner } from "lib/api"
import { datadogLogs } from "@datadog/browser-logs"

export const GET_CAMPAIGN_THRESHOLDS = gql`
  query getCampaignThresholds {
    getCampaignThresholds {
      threshold
      campaign
    }
  }
`

export type CampaignThresholdsResponse = {
  getCampaignThresholds: CampaignThresholdType[]
}

const getFreeShippingThreshold = async (): Promise<CampaignThresholdType[]> => {
  const client = getClient()

  const response = await client.query<CampaignThresholdsResponse>({
    query: GET_CAMPAIGN_THRESHOLDS,
    fetchPolicy: "cache-first",
    context: {
      sessionStrategy: "REDIRECT_TO_LOGIN",
    },
  })

  return response.data.getCampaignThresholds
}

export const useCampaignThresholds = (): {
  shippingThreshold: number | null
  samplesThreshold: number | null
  currencySymbol: SymbolType
} => {
  const [shippingThreshold, setThreshold] = useState<number | null>(null)
  const [samplesThreshold, setSamplesThreshold] = useState<number | null>(null)
  const currencySymbol = useCurrencySymbol()

  useEffect(() => {
    withGlobalSpinner(getFreeShippingThreshold)()
      .then((campaignThresholds) => {
        const shippingCampaign = campaignThresholds.find(
          (th) => th.campaign === "STANDARD_SHIPPING"
        )
        const samplesCampaign = campaignThresholds.find(
          (th) => th.campaign === "SAMPLES"
        )
        if (!shippingCampaign) {
          throw new Error("Shipping threshold not found")
        }
        setThreshold(shippingCampaign.threshold)
        setSamplesThreshold(samplesCampaign?.threshold || null)
      })
      .catch((error) => {
        datadogLogs.logger.error(
          "Failed fetching free shipping threshold",
          error as Error
        )
      })
  }, [])

  return {
    shippingThreshold,
    samplesThreshold,
    currencySymbol,
  }
}
